import apiClient from '../apiClient';
import { AlertStage, AlertDecision } from '@/types/alert';
import type { PaginatedWithMetaResponse } from '@/types/api';
import type { Alert } from '@/types/alert';
import { EntityType } from '@/types/enums';
import type { DateString } from '@/types/date';
import type { AlertsDashboardSummary } from '@/types/alertDashboard';
import type { AlertIntelligence } from '@/types/alertIntelligence';

export interface AlertQueryParams {
    page?: number;
    page_size?: number;
    stage_slugs?: AlertStage[];
    entity_types?: EntityType[];
    search?: string;
    assigned_to_me?: boolean;
    entity_name?: string;
    entity_tax_id?: string;
    /** Alert number as typed by the user — 'ALR-123' or '123'. */
    display_id?: string;
    alert_type_slugs?: string[];
    sources?: string[];
    policy_ids?: string[];
    alert_keys?: string[];
    decisions?: string[];
    sort_field?: string;
    sort_order?: number;
    date_from?: DateString;
    date_to?: DateString;
    [key: string]: any;
}

export interface AlertFilterOption {
    value: string;
    label: string;
}

export interface AlertMetadata {
    available_types: AlertFilterOption[];
    // Raw enum values; the frontend translates them to labels
    sources: string[];
    decisions: string[];
    // DB-resolved names as labels
    policies: AlertFilterOption[];
    alerts: AlertFilterOption[];
}

export default {
    getAlerts: async (params: AlertQueryParams = {}): Promise<PaginatedWithMetaResponse<Alert, AlertMetadata>> => {
        return apiClient.get('/alerts', { params });
    },

    getAlertById: async (id: string): Promise<Alert | undefined> => {
         return apiClient.get(`/alerts/${id}`);
    },

    // Resolve a KYT timeline alert (by its natural key) to the full postgres alert.
    lookupAlert: async (params: {
        policyId: string;
        taxId: string;
        alertCode: string;
        triggerDate: string;
    }): Promise<Alert | undefined> => {
         return apiClient.get('/alerts/lookup', {
             params: {
                 policy_id: params.policyId,
                 tax_id: params.taxId,
                 alert_code: params.alertCode,
                 trigger_date: params.triggerDate,
             },
         });
    },

    resolveAlert: async (id: string, payload: { decision: AlertDecision, reason: string }): Promise<Alert> => {
        return apiClient.post(`/alerts/${id}/resolve`, payload);
    },

    updateStage: async (id: string, stage_slug: AlertStage, assigneeId?: string): Promise<Alert> => {
        return apiClient.patch(`/alerts/${id}`, { stage_slug, assigned_user_id: assigneeId });
    },

    getAlertIntelligence: async (alertId: string): Promise<AlertIntelligence> => {
        return apiClient.get(`/alerts/${alertId}/intelligence`);
    },

    getDashboardSummary: async (params: {
        date_from?: DateString;
        date_to?: DateString;
        sources?: string[];
    }): Promise<AlertsDashboardSummary> => {
        return apiClient.get('/alerts/dashboard-summary', { params });
    },
};
